/**
 * versusLive — production ops view of every prediction.
 * - Live mode: polls GET /api/v1/admin/live every 2s (stat cards + newest bets, real-time feel).
 * - Browse mode / user filter: GET /api/v1/admin/live/bets (FULL permanent, paginated, filterable).
 * - User dropdown: GET /api/v1/admin/live/users → pick who to inspect.
 * - Click any user → history drawer (GET /admin/live/users/:id/history): predictions, likes, comments, badges.
 * - Export CSV / JSON of the current filter (GET /admin/live/export).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { AdminLiveBet, AdminLiveStats, AdminLiveUserHistory } from '../types/adminLive';
import './LiveBettingPage.css';

interface UserRow { id: string; username: string; email: string; betCount: number; totalStaked: number; lastBetAt: string; }

const CCY: Record<string, string> = { USD: '$', GBP: '£', CAD: 'C$', EUR: '€' };
const money = (v: number, ccy = 'USD'): string =>
  `${CCY[ccy] || ''}${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BookUsd: React.FC<{ nativeCurrency: string; usd?: number }> = ({ nativeCurrency, usd }) => (
  nativeCurrency !== 'USD' && Number.isFinite(usd)
    ? <span className="lb-book-usd">{money(usd as number, 'USD')} USD book</span>
    : null
);

const LiveBettingPage: React.FC = () => {
  const [stats, setStats] = useState<AdminLiveStats | null>(null);
  const [bets, setBets] = useState<AdminLiveBet[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [mode, setMode] = useState<'live' | 'browse'>('live');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>(''); // userId
  const [users, setUsers] = useState<UserRow[]>([]);
  const [userSearch, setUserSearch] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState<boolean>(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [drawerUser, setDrawerUser] = useState<any | null>(null);
  const [drawerLoading, setDrawerLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const PAGE = 100;
  const browsing = mode === 'browse' || !!userFilter || !!statusFilter;

  const loadStats = useCallback(async () => {
    try {
      const data = await apiService.getAdminLive();
      setStats(data.stats);
      if (!browsing) { setBets(data.recentBets || []); setTotal(data.stats.allBets || (data.recentBets || []).length); }
      setLive(true); setLastUpdate(new Date()); setError(null);
    } catch (e: any) { setLive(false); setError(e?.message || 'Failed to load live data'); }
  }, [browsing]);

  const loadBrowse = useCallback(async () => {
    try {
      const data = await apiService.getAdminLiveBets({
        userId: userFilter || undefined, status: statusFilter || undefined, limit: PAGE, offset: page * PAGE,
      });
      setBets(data.bets || []); setTotal(data.total || 0); setLive(true); setLastUpdate(new Date()); setError(null);
    } catch (e: any) { setError(e?.message || 'Failed to load bets'); }
  }, [userFilter, statusFilter, page]);

  // stat cards always poll; feed source depends on mode
  useEffect(() => {
    loadStats();
    timer.current = setInterval(loadStats, 2000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [loadStats]);

  useEffect(() => { if (browsing) loadBrowse(); }, [browsing, loadBrowse]);

  // user dropdown source
  useEffect(() => {
    const t = setTimeout(() => { apiService.getAdminLiveUsers(userSearch || undefined).then((d) => setUsers(d.users || [])).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [userSearch]);

  const openUser = async (userId?: string) => {
    if (!userId) return;
    setDrawerLoading(true); setDrawerUser({ loading: true });
    try { setDrawerUser(await apiService.getAdminUserHistory(userId)); }
    catch (e: any) { setDrawerUser({ error: e?.message || 'Failed' }); }
    finally { setDrawerLoading(false); }
  };

  const doExport = async (format: 'csv' | 'json') => {
    setExporting(true);
    try { await apiService.exportAdminBets(format, { userId: userFilter || undefined, status: statusFilter || undefined }); }
    catch (e: any) { setError(e?.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  const shown = bets;
  const selectedUser = users.find((u) => u.id === userFilter);

  return (
    <div className="live-betting-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">versusLive</h1>
          <p className="page-sub">Every prediction — live feed, per-user drill-down, export</p>
        </div>
        <div className={`lb-live ${live ? 'on' : 'off'}`}>
          <span className="lb-dot" />
          {live ? (browsing ? 'BROWSE' : 'LIVE') : 'OFFLINE'}
          {lastUpdate && <span className="lb-ts">· {lastUpdate.toLocaleTimeString()}</span>}
        </div>
      </div>

      {error && <div className="lb-error">{error}</div>}

      <div className="lb-stats">
        <div className="lb-card"><div className="lb-card-label">Total Liability</div><div className="lb-card-value">{stats ? money(stats.totalLiability) : '—'}</div><div className="lb-card-foot">USD book · across live markets</div></div>
        <div className="lb-card"><div className="lb-card-label">Active Users</div><div className="lb-card-value">{stats ? stats.activeUsers : '—'}</div><div className="lb-card-foot">distinct · last 5 min</div></div>
        <div className="lb-card"><div className="lb-card-label">Volume</div><div className="lb-card-value">{stats ? money(stats.volume5min) : '—'}</div><div className="lb-card-foot">USD book stake · last 5 min</div></div>
        <div className="lb-card"><div className="lb-card-label">All Predictions</div><div className="lb-card-value">{stats?.allBets != null ? stats.allBets.toLocaleString() : '—'}</div><div className="lb-card-foot">{stats?.allUsers != null ? `${stats.allUsers} users · all time` : 'all time'}</div></div>
      </div>

      <div className="lb-grid">
        <div className="lb-feed">
          <div className="lb-toolbar">
            <div className="lb-filter">
              <label>Mode</label>
              <select value={mode} onChange={(e) => { setMode(e.target.value as 'live' | 'browse'); setPage(0); }}>
                <option value="live">Live (newest)</option>
                <option value="browse">Browse all</option>
              </select>
            </div>
            <div className="lb-filter lb-filter-user">
              <label>User</label>
              <input list="lb-users" placeholder="all users — type to search"
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  const hit = users.find((u) => `${u.username} · ${u.email}` === e.target.value || u.username === e.target.value);
                  setUserFilter(hit ? hit.id : ''); setPage(0);
                }} />
              <datalist id="lb-users">
                {users.map((u) => <option key={u.id} value={`${u.username} · ${u.email}`}>{u.betCount} bets · {money(u.totalStaked)}</option>)}
              </datalist>
            </div>
            <div className="lb-filter">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <option value="">all</option><option value="active">active</option>
                <option value="pending_review">pending_review</option><option value="settled_win">settled_win</option>
                <option value="settled_loss">settled_loss</option><option value="cancelled">cancelled</option>
              </select>
            </div>
            <div className="lb-export">
              <button disabled={exporting} onClick={() => doExport('csv')}>⬇ CSV</button>
              <button disabled={exporting} onClick={() => doExport('json')}>⬇ JSON</button>
            </div>
          </div>

          {(userFilter || statusFilter) && (
            <div className="lb-activefilter">
              Filter: {selectedUser ? <strong>{selectedUser.username}</strong> : userFilter ? 'user' : ''} {statusFilter && <span>· {statusFilter}</span>}
              <button className="lb-clear" onClick={() => { setUserFilter(''); setUserSearch(''); setStatusFilter(''); setPage(0); }}>clear</button>
              {selectedUser && <button className="lb-clear" onClick={() => openUser(selectedUser.id)}>view history →</button>}
            </div>
          )}

          <div className="lb-count">{browsing ? `${total.toLocaleString()} predictions match` : `${shown.length} newest · ${(stats?.allBets ?? 0).toLocaleString()} all-time`}</div>

          <div className="lb-table-wrap">
            <table className="lb-table">
              <thead><tr><th>Time</th><th>User</th><th>Market</th><th>Outcome</th><th>Side</th><th className="num">Stake</th><th className="num">Payout</th><th className="num">Odds</th><th>Status</th><th>Src</th></tr></thead>
              <tbody>
                {shown.length === 0 && <tr><td colSpan={10} className="lb-empty">No predictions match.</td></tr>}
                {shown.map((b) => (
                  <tr key={b.id}>
                    <td className="lb-time">{new Date(b.at).toLocaleTimeString()}</td>
                    <td className="lb-user"><button className="lb-userbtn" title={b.email} onClick={() => openUser(b.userId)}>{b.username || '—'}</button></td>
                    <td className="lb-market" title={b.market}>{b.market}</td>
                    <td>{b.outcome}</td>
                    <td><span className={`lb-side-pill lb-side-${b.side}`}>{b.side}</span></td>
                    <td className="num lb-strong"><span>{money(b.stake, b.currency)}</span><BookUsd nativeCurrency={b.currency} usd={b.stakeUsd} /></td>
                    <td className="num lb-strong"><span>{money(b.payout, b.currency)}</span><BookUsd nativeCurrency={b.currency} usd={b.payoutUsd} /></td>
                    <td className="num">{b.odds.toFixed(2)}</td>
                    <td><span className={`lb-status lb-status-${b.status}`}>{b.status}</span></td>
                    <td className="lb-src">{b.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {browsing && total > PAGE && (
            <div className="lb-pager">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>← prev</button>
              <span>page {page + 1} / {Math.ceil(total / PAGE)}</span>
              <button disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>next →</button>
            </div>
          )}
        </div>

        <div className="lb-side">
          <div className="lb-side-title">Top markets by liability</div>
          {stats?.topMarkets?.length ? stats.topMarkets.map((m) => (
            <div className="lb-mkt" key={m.id}>
              <div className="lb-mkt-name" title={m.headline}>{m.headline}</div>
              <div className="lb-mkt-row"><span className="lb-mkt-liab">{money(m.liability)} USD</span><span className="lb-mkt-staked">USD staked {money(m.staked)}</span></div>
            </div>
          )) : <div className="lb-empty">No live markets.</div>}
        </div>
      </div>

      {drawerUser && (
        <div className="lb-drawer-overlay" onClick={() => setDrawerUser(null)}>
          <div className="lb-drawer" onClick={(e) => e.stopPropagation()}>
            <button className="lb-drawer-close" onClick={() => setDrawerUser(null)}>✕</button>
            {drawerLoading || drawerUser.loading ? <div className="lb-empty">Loading history…</div>
              : drawerUser.error ? <div className="lb-error">{drawerUser.error}</div>
                : <UserHistory data={drawerUser} money={money} />}
          </div>
        </div>
      )}
    </div>
  );
};

const UserHistory: React.FC<{ data: AdminLiveUserHistory; money: (v: number, c?: string) => string }> = ({ data, money }) => {
  const [tab, setTab] = useState<'predictions' | 'likes' | 'comments' | 'badges'>('predictions');
  const u = data.user, s = data.summary;
  return (
    <div className="lb-hist">
      <div className="lb-hist-head">
        <div className="lb-hist-name">{u.username}</div>
        <div className="lb-hist-email">{u.email} · joined {new Date(u.joinedAt).toLocaleDateString()} · KYC {u.kycStatus}</div>
        <div className="lb-hist-bal">Balance {money(u.balance, u.currency || 'USD')} · Bonus {money(u.bonusBalance, u.currency || 'USD')}</div>
      </div>
      <div className="lb-hist-stats">
        <span><b>{s.predictions}</b> predictions</span><span><b>{money(s.staked)}</b> USD book staked</span>
        <span><b>{s.wins}</b>W / <b>{s.losses}</b>L</span>{s.pendingReview > 0 && <span className="lb-warn"><b>{s.pendingReview}</b> pending</span>}
        <span><b>{s.marketLikes}</b> likes</span><span><b>{s.comments}</b> comments</span><span><b>{s.badges}</b> badges</span>
      </div>
      <div className="lb-hist-tabs">
        {(['predictions', 'likes', 'comments', 'badges'] as const).map((t) => (
          <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>{t} ({data[t]?.length ?? 0})</button>
        ))}
      </div>
      <div className="lb-hist-body">
        {tab === 'predictions' && (data.predictions || []).map((b) => (
          <div className="lb-hist-row" key={b.id}><span>{new Date(b.at).toLocaleString()}</span><span className="lb-hist-mkt">{b.market}</span><span>{b.outcome} · {b.side.toUpperCase()}</span><span className="lb-strong lb-money-stack"><span>{money(b.stake, b.currency)} → {money(b.payout, b.currency)}</span><BookUsd nativeCurrency={b.currency} usd={b.payoutUsd} /></span><span className={`lb-status lb-status-${b.status}`}>{b.status}</span></div>
        ))}
        {tab === 'likes' && (data.likes || []).map((l, i) => (<div className="lb-hist-row" key={i}><span>{new Date(l.at).toLocaleString()}</span><span className="lb-hist-mkt">{l.market}</span></div>))}
        {tab === 'comments' && (data.comments || []).map((c) => (<div className="lb-hist-row" key={c.id}><span>{new Date(c.at).toLocaleString()}</span><span className="lb-hist-mkt">{c.market}</span><span className="lb-hist-body-txt">“{c.body}”</span><span>♥ {c.likes}</span></div>))}
        {tab === 'badges' && (data.badges || []).map((bd) => (<div className="lb-hist-row" key={bd.id}><span>{new Date(bd.at).toLocaleDateString()}</span><span>{bd.icon} {bd.name || bd.badgeId}</span><span>{bd.tier}</span></div>))}
        {(!data[tab] || data[tab].length === 0) && <div className="lb-empty">No {tab}.</div>}
      </div>
    </div>
  );
};

export default LiveBettingPage;
